<template>
  <BasePopup
    ref="popup"
    :title="title"
    class="cancel-flow-popup"
    @hide="onHide"
  >
    <!--
      Each step is a self-contained component that renders its own footer
      buttons; this host only drives the flow between steps.
    -->
    <CancelFlowIntro
      v-if="step === 'intro'"
      :billing="billing"
      :loading="registering"
      @keep="hide"
      @continue="onIntroContinue"
    />
    <CancelFlowDiscount
      v-else-if="step === 'discount'"
      :billing="billing"
      @accepted="hide"
      @decline="step = 'reasons'"
    />
    <CancelFlowReasons
      v-else-if="step === 'reasons'"
      :billing="billing"
      :canceling="canceling"
      @reasonSelected="onReasonSelected"
      @continueCancel="onContinueCanceling"
    />
    <CancelFlowPauseFeature
      v-else-if="step === 'feature'"
      :billing="billing"
      :canceling="canceling"
      :reason="selectedReason"
      @feedback="onFeedback"
      @continueCancel="onContinueCanceling"
    />
    <CancelFlowRelevantBooks
      v-else-if="step === 'books'"
      :billing="billing"
      :canceling="canceling"
      :reason="selectedReason"
      @continueCancel="onContinueCanceling"
    />
    <CancelFlowFeedback
      v-else
      :billing="billing"
      :canceling="canceling"
      :reason="selectedReason"
      @send="onFeedback"
      @continueCancel="onContinueCanceling"
    />
  </BasePopup>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import BasePopup from '@/components/ui/BasePopup.vue'
import CancelFlowIntro from './CancelFlowIntro.vue'
import CancelFlowReasons from './CancelFlowReasons.vue'
import CancelFlowRelevantBooks from './CancelFlowRelevantBooks.vue'
import CancelFlowPauseFeature from './CancelFlowPauseFeature.vue'
import CancelFlowFeedback from './CancelFlowFeedback.vue'
import CancelFlowDiscount from './CancelFlowDiscount.vue'

import { BillingView } from '@/models/billing'
import type { ReasonValue } from '@/models/billing.cancelFlow'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { Mixpanel } from '@/analytics/mixpanel'

type Step = 'intro' | 'discount' | 'reasons' | 'feature' | 'books' | 'feedback'

// The follow-up step for a reason; reasons without one cancel directly.
const REASON_STEPS: Partial<Record<ReasonValue, Step>> = {
  not_finding_time: 'feature',
  not_enough_books: 'books',
  content_short: 'feedback',
  app_short: 'feedback',
  something_else: 'feedback',
}

const props = defineProps<{
  billing: BillingView
}>()

const popup = ref<InstanceType<typeof BasePopup> | null>(null)
const step = ref<Step>('intro')
const canceling = ref(false)
const registering = ref(false)
const selectedReason = ref<ReasonValue | null>(null)
// The id of the answer saved on the reason click (null if the save failed).
let savedAnswer: Promise<string | null> | null = null

const title = computed(() => {
  switch (step.value) {
    case 'intro':
      return 'Are you sure you want to end your subscription?'
    case 'reasons':
      return 'Before you go,<br>can you tell us why?'
    case 'feedback':
      return 'What could we do better?'
    // The discount, pause and books steps render their own heading.
    default:
      return ''
  }
})

function show(): void {
  step.value = 'intro'
  selectedReason.value = null
  savedAnswer = null
  popup.value?.show()
}

function hide(): void {
  popup.value?.hide(false)
}

// Track when the user dismisses the flow (X or click outside). Programmatic
// closes pass trackEvent=false, so they don't fire this.
function onHide(trackEvent: boolean): void {
  if (trackEvent) {
    Mixpanel.trackCancelFlowClosed(
      props.billing,
      selectedReason.value,
      step.value,
    )
  }
}

/**
 * Register the cancel discount. The backend registers it only for users who
 * can get it (trial Stripe users without another discount), everyone else
 * goes to the reasons step.
 */
async function onIntroContinue(): Promise<void> {
  try {
    registering.value = true
    const registered = await backend.registerCancelDiscount()
    step.value = registered ? 'discount' : 'reasons'
  } finally {
    registering.value = false
  }
}

/**
 * Save the reason right away, so it isn't lost if the user leaves the
 * follow-up step without answering, then open that step or cancel.
 */
function onReasonSelected(reason: ReasonValue): void {
  selectedReason.value = reason
  savedAnswer = backend.saveCancelFlowAnswer(reason).catch((err) => {
    Sentry.captureException(err)
    return null
  })

  const nextStep = REASON_STEPS[reason]
  Mixpanel.trackCancelReasonSubmitted(
    props.billing,
    reason,
    nextStep ?? 'cancel',
  )
  if (nextStep) {
    step.value = nextStep
  } else {
    onContinueCanceling()
  }
}

// A failing save shouldn't block the cancellation.
async function onFeedback(feedback: string): Promise<void> {
  await Promise.all([saveFeedback(feedback), onContinueCanceling()])
}

async function saveFeedback(feedback: string): Promise<void> {
  const reason = selectedReason.value
  if (!reason || !feedback) {
    return
  }
  try {
    const answerId = await savedAnswer
    await backend.saveCancelFlowAnswer(reason, feedback, answerId ?? undefined)
  } catch (err) {
    Sentry.captureException(err)
  }
}

/**
 * End the subscription (switch to the free plan). Closes the popup only on
 * success, so a failure keeps it open for a retry.
 */
async function onContinueCanceling(): Promise<void> {
  try {
    canceling.value = true
    await props.billing.subscribeTo(props.billing.freePlan)
    hide()
  } catch (err) {
    Sentry.captureException(err)
    alert('Failed to cancel the subscription')
  } finally {
    canceling.value = false
  }
}

defineExpose({ show, hide })
</script>

<style lang="scss" scoped>
// All of the save-flow popup styles are here.
.cancel-flow-popup {
  --popup-width: 520px;

  :deep(.popup__window) {
    padding-inline: 48px;
    padding-block: 48px 0;
  }

  :deep(.cancel-flow-intro) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
    text-align: center;

    .cancel-flow-intro__divider {
      width: 100%;
      height: 1px;
      background: $c-border-2;
    }

    .cancel-flow-intro__image {
      width: 360px;
      max-width: 100%;
      height: auto;
    }

    p {
      margin: 0;
      font-size: 16px;
      line-height: 22px;
      letter-spacing: -0.02em;
      color: $c-black-15;
    }
  }

  :deep(.cancel-flow-reasons__list) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 24px;
    padding: 0;
  }

  :deep(.cancel-flow-reasons__item) {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 20px 24px 20px 20px;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.02em;
    text-align: left;
    color: $c-black-2;
    background: $c-white;
    border: 1px solid $c-border-2;
    border-radius: 14px;
    cursor: pointer;
    transition: border-color 0.15s ease;

    @include hover {
      border-color: $c-yellow;
    }
  }

  :deep(.cancel-flow-pause-feature) {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .cancel-flow-pause-feature__image {
      width: 415px;
      max-width: 100%;
      height: auto;
    }

    .cancel-flow-pause-feature__title {
      max-width: 504px;
      margin-top: 28px;
      font-size: 24px;
      line-height: 120%;
      letter-spacing: -0.02em;
      color: $c-black-2;
    }

    .cancel-flow-pause-feature__feedback {
      display: flex;
      justify-content: center;
      width: 100%;
      max-width: 327px;
      margin-top: 36px;
      border: 1px solid rgba($c-black-2, 0.1);
      border-radius: 14px;
    }

    .cancel-flow-pause-feature__feedback-divider {
      width: 1px;
      background: $c-border-2;
    }

    .cancel-flow-pause-feature__feedback-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding-block: 20px;
      background: transparent;
      border: none;
      border-radius: 14px;
      cursor: pointer;
      transition: background 0.15s ease;

      @include hover {
        background: rgba($c-black-2, 0.05);
      }

      .iconfont {
        width: 40px;
        height: 40px;
        font-size: 40px;
        line-height: 1;
      }

      .idislike {
        color: $c-red-6;
      }

      .ilike {
        color: $c-green-2;
      }
    }
  }

  :deep(.cancel-flow-relevant-books) {
    .cancel-flow-relevant-books__header {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 24px;
      max-width: 360px;
      margin: 0 auto;
      text-align: center;
    }

    .cancel-flow-relevant-books__title {
      font-size: 24px;
      line-height: 120%;
      letter-spacing: -0.02em;
      color: $c-black-2;
    }

    .cancel-flow-relevant-books__subtitle {
      margin: 0;
      font-size: 16px;
      line-height: 130%;
      color: $c-black-3;
    }

    // Reserve roughly the loaded-list height so the popup doesn't jump
    // between the loading state and the rendered books.
    .cancel-flow-relevant-books__loading,
    .cancel-flow-relevant-books__list {
      min-height: 320px;
      margin-top: 12px;
    }

    .cancel-flow-relevant-books__loading {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cancel-flow-relevant-books__list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-height: 400px;
      padding: 0 0 2px;
      overflow-y: auto;
    }

    .cancel-flow-relevant-books__item {
      display: flex;
      align-items: stretch;
      gap: 16px;
      border: 1px solid $c-border-2;
      border-radius: 12px;
      overflow: hidden;
      text-decoration: none;
      cursor: pointer;

      @include hover {
        background: rgba($c-black-2, 0.04);
      }
    }

    .cancel-flow-relevant-books__cover {
      flex: 0 0 auto;
      width: 72px;
      padding: 8px;
    }

    .cancel-flow-relevant-books__info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 4px;
      min-width: 0;
      padding-block: 12px;
      padding-right: 16px;
    }

    .cancel-flow-relevant-books__book-title {
      margin: 0;
      overflow: hidden;
      font-size: 16px;
      line-height: 1.3;
      color: $c-black-2;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .cancel-flow-relevant-books__book-author {
      margin: 0;
      font-size: 13px;
      color: $c-black-3;
    }
  }

  :deep(.cancel-flow-feedback) {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 16px;

    .cancel-flow-feedback__subtitle {
      margin: 0;
      font-size: 16px;
      line-height: 130%;
      text-align: center;
      color: $c-black-3;
    }

    .cancel-flow-feedback__field {
      width: 100%;
      max-width: 424px;
      height: 294px;
      margin-top: 24px;
      padding: 16px;
      font-size: 16px;
      line-height: 22px;
      color: $c-black-2;
      background: $c-black-5;
      border: 1px solid $c-black-4;
      border-radius: 8px;
      resize: none;

      &::placeholder {
        color: $c-black-6;
      }
    }

    .cancel-flow-feedback__support {
      margin-top: 16px;
      font-size: 14px;
      color: $c-black-3;
      text-decoration: underline;
    }
  }

  :deep(.cancel-flow-discount) {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .cancel-flow-discount__image {
      max-width: 100%;
      height: auto;
    }

    .cancel-flow-discount__heading {
      max-width: 420px;
      margin-top: 28px;
      font-size: 28px;
      font-weight: 700;
      line-height: 120%;
      letter-spacing: -0.02em;
      color: $c-black-2;
    }

    .cancel-flow-discount__subheading {
      margin: 8px 0 0;
      font-size: 18px;
      line-height: 130%;
      color: $c-black-3;
    }

    .cancel-flow-discount__price {
      margin: 16px 0 0;
      font-size: 24px;
      font-weight: 700;
      line-height: 120%;
      color: $c-blue-4;
    }

    .cancel-flow-discount__original {
      margin: 4px 0 0;
      font-size: 16px;
      line-height: 130%;
      color: $c-black-3;
      text-decoration: line-through;
    }

    .cancel-flow-discount__disclaimer {
      max-width: 420px;
      margin: 24px 0 0;
      font-size: 12px;
      line-height: 140%;
      color: $c-black-3;
    }
  }

  // The footer lives inside each step component, so it has to be reached
  // with :deep from this host.
  :deep(.cancel-flow-popup__footer) {
    position: sticky;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-block: 48px 40px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      $c-white 14%
    );
    backdrop-filter: blur(5px);
    mask-image: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 1) 14%
    );
  }

  :deep(.cancel-flow-popup__cta) {
    width: 100%;
  }

  @media (max-width: $w-medium-s) {
    :deep(.popup__window) {
      padding-inline: 24px;
    }
  }
}

.night-mode .cancel-flow-popup {
  :deep(.cancel-flow-intro) {
    p {
      color: $c-black-11;
    }

    .cancel-flow-intro__divider {
      background: $c-dark-5;
    }
  }

  :deep(.cancel-flow-reasons__item) {
    color: $c-black-11;
    background: $c-black-8;
    border-color: $c-dark-5;

    @include hover {
      border-color: $c-yellow;
    }
  }

  :deep(.cancel-flow-pause-feature) {
    .cancel-flow-pause-feature__title {
      color: $c-black-11;
    }

    .cancel-flow-pause-feature__feedback {
      border-color: rgba($c-white, 0.1);
    }

    .cancel-flow-pause-feature__feedback-divider {
      background: $c-dark-5;
    }

    .cancel-flow-pause-feature__feedback-btn {
      @include hover {
        background: rgba($c-white, 0.05);
      }
    }
  }

  :deep(.cancel-flow-relevant-books) {
    .cancel-flow-relevant-books__title,
    .cancel-flow-relevant-books__book-title {
      color: $c-black-11;
    }

    .cancel-flow-relevant-books__subtitle,
    .cancel-flow-relevant-books__book-author {
      color: $c-dark-7;
    }

    .cancel-flow-relevant-books__item {
      border-color: $c-dark-5;

      @include hover {
        background: rgba($c-white, 0.06);
      }
    }
  }

  :deep(.cancel-flow-feedback) {
    .cancel-flow-feedback__subtitle,
    .cancel-flow-feedback__support {
      color: $c-dark-7;
    }

    .cancel-flow-feedback__field {
      color: $c-black-11;
      background: $c-black-8;
      border-color: $c-dark-5;
    }
  }

  :deep(.cancel-flow-discount) {
    .cancel-flow-discount__heading {
      color: $c-black-11;
    }

    .cancel-flow-discount__price {
      color: $c-blue-5;
    }

    .cancel-flow-discount__subheading,
    .cancel-flow-discount__original,
    .cancel-flow-discount__disclaimer {
      color: $c-dark-7;
    }
  }

  :deep(.cancel-flow-popup__footer) {
    background: linear-gradient(180deg, rgba($c-black-7, 0) 0%, $c-black-7 14%);
  }
}
</style>
